import { ChartExportRecord, formatGmtOffsetLabel, recordUtcOffsetMinutes } from './chart-export.models';
import { MAIA_ONLINE_CSV_HEADER } from '../import/maia-online-csv.parser';

/**
 * Schrijft charts in het CSV-formaat van de Maia Mechanics Online-export (zelfde kolommen
 * `Name,Date,Time,Timezone,City,Country`; zie docs/human-design/hd-chart-import.md §2 voor de
 * semantiek, die hier in omgekeerde richting geldt):
 *  - `Time` = lokale kloktijd, `Timezone` = de vaste offset op het geboortemoment ("GMT+1",
 *    "GMT-3:30", kaal "GMT" voor 0), zoals de dropdownwaarde van native online-charts.
 *  - Een chart zonder echte lokale tijd (geen locatie of onbekende tijdzone) krijgt de tijd in
 *    UTC en een LEGE Timezone-kolom: zo lezen Maia Online en onze eigen import die tijd als UTC.
 *    Een "GMT"-label zou daar fout zijn: onze import rekent een gelabelde tijd kloktijd-eerst
 *    op de gevonden plaats om, en schuift de chart dan op.
 *  - `City` enkel de stad, `Country` de ISO-2-code (of de landtekst wanneer die ontbreekt).
 * Regeleinden LF en geen BOM, net als de echte export.
 */
export function buildMaiaOnlineCsv(records: readonly ChartExportRecord[]): string {
  const header = 'Name,Date,Time,Timezone,City,Country';
  // De header moet byte-gelijk blijven aan wat de importdetectie herkent.
  if (header.toLowerCase() !== MAIA_ONLINE_CSV_HEADER) throw new Error('Maia Online CSV header drift');

  const rows = records.map(record => {
    const clock = record.birthLocal ?? record.birthUtc;
    return [
      record.name,
      clock.format('YYYY-MM-DD'),
      clock.format('HH:mm:ss'),
      record.birthLocal ? formatGmtOffsetLabel(recordUtcOffsetMinutes(record)) : '',
      record.city ?? '',
      record.countryCode ?? record.countryName ?? '',
    ]
      .map(csvField)
      .join(',');
  });
  return [header, ...rows].join('\n') + '\n';
}

/** RFC 4180: aanhalingstekens rond velden met komma of aanhalingsteken; regeleinden worden spaties. */
function csvField(value: string): string {
  const singleLine = value.replace(/[\r\n]+/g, ' ');
  return /[",]/.test(singleLine) ? `"${singleLine.replace(/"/g, '""')}"` : singleLine;
}
